import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';
import TableTree, { Rows } from '@atlaskit/table-tree';
import { connect } from 'react-redux';

import { Console } from '../../../tools';
import Header from './header';
import Helper from './helpers';
import Preferences from './preferences';
import Rule from './rule';
import Styled from './styled';
import { Table as API } from '../../../server';

class SchemaGenerator extends React.Component {
  constructor(props) {
    super(props);
    this.data = {};
    this.schema = this.props.schema;
    this.console = new Console({
      dev: this.props.debug,
      name: this.props.title,
    });
    this.state = {
      update: 1,
      message: false,
      columns: [],
    };

    this.add = this.add.bind(this);
    this.message = this.message.bind(this);
    this.onDatabaseTableChange = this.onDatabaseTableChange.bind(this);
    this.output = this.output.bind(this);
    this.remove = this.remove.bind(this);
    this.sort = this.sort.bind(this);
    this.synchronize = this.synchronize.bind(this);
  }

  componentWillMount() {
    this.console.log('Ready');
  }

  shouldComponentUpdate(nextProps, nextState) {
    return Helper.comparison(this.state, nextState);
  }

  componentDidUpdate() {
    this.console.log('Rendered');
  }

  onDatabaseTableChange(table) {
    this.console.log('Database table changed');
    if (table) {
      this.schema.table = table;
      this.data.table = table; // Used to pull columns
      API.column({
        data: {
          table: this.schema.table,
        },
        success: (data) => {
          this.setState({
            columns: data.map(elem => elem.ColumnName),
          });
          this.output();
        },
      });
    }
  }

  add(rules) {
    const max = 10;
    const limit = 50;

    if ((this.schema.rules.length + rules.length) <= limit) {
      if (rules.length <= max) {
        const assign = [...this.schema.rules, ...rules];
        this.console.group('Added new rule', () => {
          this.console.info('#p prev data', this.schema.rules);
          this.console.info('#a data', rules);
          this.console.info('#n next data', assign);
        });
        this.schema.rules = assign;
        this.update();
      } else {
        this.setState({
          message: Helper.message(`You can add up to ${max} rows at a time.`),
        });
      }
    } else {
      this.setState({
        message: Helper.message(`Total line length can not exceed ${limit} lines. ${limit - this.schema.rules.length} lines can be added.`),
      });
    }
  }

  remove(key) {
    let rules;

    if (key === 'DELETE_ALL') {
      rules = [];
    } else {
      rules = this.schema.rules.filter(elem => elem.uuid !== key);
    }

    if (rules.length < 1) {
      rules.push({ uuid: uuid() });
    }

    this.console.group('Remove', () => {
      this.console.info('#p prev data', this.schema.rules);
      this.console.info('#a data', key);
      this.console.info('#n next data', rules);
    });

    this.schema.rules = rules;
    this.update();
    this.output();
  }

  message(message) {
    this.setState({ message });
  }

  update() {
    this.setState({
      message: false,
      update: new Date().getTime(),
    });
  }

  sort(type) {
    this.schema.rules.sort((current, next) => {
      if (current.cell === next.cell) {
        if (type === 'dec') return current.row > next.row ? -1 : 1;
        return current.row < next.row ? -1 : 1;
      }
      if (type === 'dec') return current.cell > next.cell ? -1 : 1;
      return current.cell < next.cell ? -1 : 1;
    });

    this.output();
    this.update();
  }

  synchronize(key, data) {
    const current = this.schema.rules.find(item => item.uuid === key);
    const condition = Helper.comparison(data, current);

    if (condition) {
      const rules = this.schema.rules.map((rule) => {
        if (rule.uuid === key) return { ...rule, ...data };
        return rule;
      });

      this.console.group('Synchronize', () => {
        this.console.info('#p prev data', this.schema.rules);
        this.console.info('#a data', data);
        this.console.info('#n next data', rules);
      });

      this.schema.rules = rules;
      this.output();
    }
  }

  output() {
    this.console.log('Output has been updated');
    this.props.output(this.schema);
  }

  render() {
    return (
      <Styled.SchemaGenerator data-update={this.state.update}>
        <div style={{ marginBottom: 20 }}>
          {this.state.message}
        </div>
        <Preferences
          add={this.add}
          change={this.onDatabaseTableChange}
          console={this.console}
          remove={this.remove}
          sort={this.sort}
          table={this.schema.table}
        />
        <TableTree>
          <Header />
          <Rows
            items={this.schema.rules}
            render={(elem) => {
              const props = {
                rules: this.schema.rules,
                ...elem,
                columns: this.state.columns,
                console: this.console,
                key: elem.uuid,
                remove: this.remove,
                synchronize: this.synchronize,
              };
              return <Rule {...props}>Rule</Rule>;
            }}
          />
        </TableTree>
      </Styled.SchemaGenerator>
    );
  }
}

SchemaGenerator.propTypes = {
  debug: PropTypes.bool,
  output: PropTypes.func,
  schema: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  title: PropTypes.string,
};

SchemaGenerator.defaultProps = {
  debug: false,
  output: () => true,
  schema: {
    table: '',
    rules: [{
      uuid: uuid(),
    }],
  },
  title: 'Schema Generator',
};

const mapStateToProps = state => ({
  columns: state.Column,
});

export default connect(mapStateToProps)(SchemaGenerator);
